import {
  communitySlug,
  isDocumentsEnabled,
  members,
  spaceList,
  spaces,
} from "app/stores/community";
import { get } from "svelte/store";
import tippy from "tippy.js";
import SpaceList from "./SpaceList.svelte";
import MemberList from "./MemberList.svelte";
import { isCommunityEnabled } from "app/stores/community";
import { isDarkTheme } from "app/stores/userPreferences";
import type { SuggestionOptions } from "@tiptap/suggestion";
import type { Member } from "models/community";
import type { GenericSpace } from "models/spaces";
import { type Stores } from "$stores";

const suggestion = ({
  transform,
  dataFetch,
  listComponent,
}: {
  dataFetch: () => any[] | Promise<any[]>;
  transform: (datum: any) => string;
  listComponent: typeof SpaceList | typeof MemberList;
}): Partial<SuggestionOptions> => {
  return {
    items: async ({ query }: { query: string }) => {
      const lowerQuery = query.toLowerCase();
      const data = await dataFetch();
      return data.map((items) =>
        items.filter((item: any) => transform(item).toLowerCase().includes(lowerQuery)).slice(0, 10)
      );
    },

    render: () => {
      let component: SpaceList | MemberList | null;
      let popup: any[];

      return {
        onStart: (props) => {
          // ensure we have a clientRect before rendering with tippy
          if (!props.clientRect) {
            return;
          }
          const el = document.createElement("div");
          component = new listComponent({
            // issue here it that items prop does not match the type required by SpaceList / MemberList
            // @ts-ignore
            props: {
              ...props,
              isCommunityEnabled: get(isCommunityEnabled),
              isDocumentsEnabled: get(isDocumentsEnabled),
              isDarkMode: get(isDarkTheme),
            },
            target: el,
          });

          // @ts-ignore - clientRect incorrect typing
          // https://github.com/ueberdosis/tiptap/issues/2795
          popup = tippy("body", {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: el,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
          });
        },

        onUpdate(props) {
          // @ts-ignore
          component?.$set(props);

          if (!props.clientRect) {
            return;
          }

          popup?.[0].setProps({
            getReferenceClientRect: props.clientRect,
          });
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            popup?.[0].hide();
            return true;
          }

          if (["ArrowUp", "ArrowDown", "Enter"].includes(props.event.key)) {
            // ignore keypress as we are already handling these in Space/MemberList.svelte
            return true;
          }

          // @ts-ignore
          return false;
        },

        onExit() {
          popup?.[0].destroy();
          component?.$destroy();
        },
      };
    },
  };
};

export const memberSuggestion = () =>
  suggestion({
    dataFetch: () => [get(members)],
    transform: (item: Member) => item.handle ?? "",
    listComponent: MemberList,
  });
export const spaceSuggestion = (stores: Stores) =>
  suggestion({
    dataFetch: async () => {
      const promises: Promise<void>[] = [];
      // fetch space list if community feature is enabled and not already fetched
      if (get(isCommunityEnabled) && !get(spaces).fetched) {
        promises.push(spaces.updateSpaceGroups(get(communitySlug)));
      }
      // fetch documents if documents feature is enabled and not already fetched
      if (get(isDocumentsEnabled) && !Object.values(get(stores.docs._documents)).length) {
        promises.push(stores.docs.fetch());
      }
      await Promise.all(promises);
      return [
        Object.values(get(stores.docs._documents)),
        get(isCommunityEnabled) ? get(spaceList) : [],
      ];
    }, // only fetch space list if community feature is enabled
    transform: (item: GenericSpace) => item.title ?? "",
    listComponent: SpaceList,
  });

export default suggestion;
